import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { defaultButtonClasses } from "@/constants/ui";
import { cn } from "@/lib/utils";
import AmenitiesRenderer from "../../AmenityIconRenderer/AmenitiesRenderer";
import RoomGallery from "../../RoomGallery/RoomGallery";
import RoomSwatch from "../../RoomSwatch/RoomSwatch";
import type { Amenity } from "@/types/Amenity";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";
import { formatRoomCapacity } from "@/utils/roomCapacity";

type RoomDialogProps = {
  room: Room;
  location: Location;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const RoomDialog = ({ room, location, open, onOpenChange }: RoomDialogProps) => {
  if (!room || !location) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[95vh]">
        <ScrollArea className="max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-3xl">
              Our {room?.name} room at {location?.city} {location?.name}
            </DialogTitle>
            <DialogDescription className="text-base">
              Details about the {room?.name} room at {location?.city} {location?.name}. If you need to know more about
              this room, contact the practice manager.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 px-1">
            <RoomGallery gallery={room?.gallery || []} inModal />
          </div>
          <dl className="mt-4 text-gray-900 divide-y divide-gray-200 dark:text-white dark:divide-gray-700">
            <div className="flex flex-col py-3">
              <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Description</dt>
              <dd className="text-base font-semibold">{room?.description || "No description available."}</dd>
            </div>
            {room?.amenities && (
              <div className="flex flex-col py-3">
                <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Amenities</dt>
                <dd className="text-lg font-semibold">
                  <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                    <AmenitiesRenderer amenities={room?.amenities as Amenity[]} />
                  </div>
                </dd>
              </div>
            )}
            <div className="flex flex-col py-3">
              <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Room Name</dt>
              <dd className="text-lg font-semibold">
                <RoomSwatch swatch={room?.swatch ?? "bg-gray-500"} name={room?.name ?? "Room"} />
                <span className="md:hidden"> {room?.name}</span>
              </dd>
            </div>
            <div className="flex flex-col py-3">
              <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Room Floor</dt>
              <dd className="text-lg font-semibold capitalize">{room?.floor || "not specified"}</dd>
            </div>
            <div className="flex flex-col py-3">
              <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Capacity</dt>
              <dd className="text-lg font-semibold">{formatRoomCapacity(room)}</dd>
            </div>
            <div className="flex flex-col py-3">
              <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Is accessible?</dt>
              <dd className="text-lg font-semibold">
                {room?.isAccessible ? "Yes, the room is accessible." : "No, the room is not accessible."}
              </dd>
            </div>
          </dl>
          <div className="mt-8 flex w-full">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className={cn("w-full", defaultButtonClasses)}
            >
              Close
            </Button>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
export default RoomDialog;
